import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import {
  EvChargingJobRow,
  EvChargingJobStatusFilter,
} from '../../../../core/models/cityparking/ev-charging-jobs.model';
import { MapperEvChargingJobsService } from '../../../../core/mappers/cityparking/mapper-ev-charging-jobs.service';
import { ApiEvChargingJobsService } from '../../../../core/services/api/cityparking/api-ev-charging-jobs.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-ev-charging-jobs',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './ev-charging-jobs.component.html',
  styleUrl: './ev-charging-jobs.component.css',
})
export class EvChargingJobsComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiEvChargingJobsService);
  private readonly mapper = inject(MapperEvChargingJobsService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  jobs: EvChargingJobRow[] = [];
  isLoading = false;
  isStopping = false;
  lastFetchedAt: Date | null = null;
  runningCount = 0;

  jobStatusFilter: EvChargingJobStatusFilter = 'all';
  startDate = '';
  endDate = '';

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  userId = 0;

  isConfirmStopOpen = false;
  pendingStopJob: EvChargingJobRow | null = null;

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  activityHistoryJobId = 0;

  constructor() {
    const today = new Date();
    this.startDate = today.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
      });
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
    }
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canEdit(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const max = Math.min(this.totalPages, 7);
    const pages: number[] = [];
    let start = Math.max(1, this.currentPage - 3);
    const end = Math.min(this.totalPages, start + max - 1);
    start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  get stopConfirmHighlight(): string {
    const job = this.pendingStopJob;
    if (!job) return '';
    const plate = job.licensePlate || '—';
    const jobId = job.jobId || String(job.id);
    return `${plate} · Job ${jobId}`;
  }

  setJobStatusFilter(status: EvChargingJobStatusFilter): void {
    if (this.jobStatusFilter === status) return;
    this.jobStatusFilter = status;
    this.currentPage = 1;
    this.loadTable();
  }

  onDateChange(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onRefresh(): void {
    // Keep jobStatusFilter — refresh must not reset the primary status chip.
    this.currentPage = 1;
    this.loadTable();
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!value || value === this.pageSize) return;
    this.pageSize = value;
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadTable();
  }

  trackById(_: number, item: EvChargingJobRow): number {
    return item.id;
  }

  statusLabel(status: string): string {
    return this.mapper.statusLabel(status);
  }

  formatDateTime(value: string | null): string {
    if (!value || value.startsWith('0001')) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hour = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hour}:${min}`;
  }

  canStop(job: EvChargingJobRow): boolean {
    return this.canEdit && (job.isRunning || job.jobStatus?.toLowerCase() === 'running');
  }

  openStopConfirm(job: EvChargingJobRow): void {
    if (!this.canStop(job)) {
      if (!this.canEdit) this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.pendingStopJob = job;
    this.isConfirmStopOpen = true;
  }

  closeStopConfirm(): void {
    this.isConfirmStopOpen = false;
    this.pendingStopJob = null;
  }

  confirmStop(): void {
    const job = this.pendingStopJob;
    this.isConfirmStopOpen = false;
    this.pendingStopJob = null;
    if (!job) return;

    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isStopping = true;
    this.api
      .updateEvChargingJobStatus({
        userId: this.userId,
        menuId,
        id: job.id,
        jobStatus: 'completed',
      })
      .subscribe({
        next: (res) => {
          this.isStopping = false;
          if (res?.isSuccess) {
            this.toast.success(res.message || 'หยุด job เรียบร้อย');
            this.loadTable();
          } else {
            this.toast.error(res?.message || 'หยุด job ไม่สำเร็จ');
          }
        },
        error: (err) => {
          this.isStopping = false;
          this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
        },
      });
  }

  openActivityHistory(jobId: number): void {
    if (!jobId || jobId <= 0) return;
    this.activityHistoryJobId = jobId;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
    this.activityHistoryJobId = 0;
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) return;
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาระบุช่วงวันที่');
      return;
    }

    this.isLoading = true;
    this.api
      .getEvChargingJobsForTable({
        siteId,
        userId: this.userId,
        startDate: this.startDate,
        endDate: this.endDate,
        jobStatus: this.jobStatusFilter,
        page: this.currentPage,
        pageSize: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTableFromDto(res);
          this.jobs = mapped.items;
          this.totalItems = mapped.total;
          this.totalPages = mapped.totalPages || 1;
          this.currentPage = mapped.page || this.currentPage;
          this.pageSize = mapped.pageSize || this.pageSize;
          this.runningCount = mapped.runningCount;
          this.lastFetchedAt = new Date();
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.jobs = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.runningCount = 0;
          this.toast.error(err?.error?.message || 'โหลดข้อมูลไม่สำเร็จ');
        },
      });
  }
}
